import { createContext, ReactNode, useContext } from "react";

import { CheckIcon, ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import i18n from "../../i18n";
import { FitmentRowResult, IInboxConversation, InboxChannel } from "../../models/messagesInbox/IInboxConversation";
import classNames from "../../utils/classNames";
import { isLateDelivery } from "../../utils/orderStage";
import { ReplyByTone } from "../../utils/replyByLabel";
import Card from "../shared/Card/Card";
import LoadingWheel from "../shared/LoadingWheel";
import RegPlate from "../shared/RegPlate";
import StatusPill, { StatusPillColour } from "../shared/StatusPill";

export const CHANNEL_LOGOS: Record<InboxChannel, { src: string; name: string }> = {
  ebay: { src: "/ebayEmblem.png", name: "eBay" },
  partpro: { src: "/partProIcon.svg", name: "PartPro" },
};

/** Channel logo; `short` shows the logo alone (with the name as its tooltip). */
export function ChannelBadge({ channel, short = false }: { channel: InboxChannel; short?: boolean }) {
  const logo = CHANNEL_LOGOS[channel];
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 text-[12px] font-semibold text-gray-800" title={logo.name}>
      <img src={logo.src} alt={logo.name} className={classNames("shrink-0 object-contain", short ? "h-4 w-4" : "h-5 w-5")} />
      {!short && logo.name}
    </span>
  );
}

const replyByColours: Record<ReplyByTone, StatusPillColour> = {
  neutral: StatusPillColour.Neutral,
  amber: StatusPillColour.Orange,
  red: StatusPillColour.Red,
};

export function ReplyByChip({ text, tone }: { text: string; tone: ReplyByTone }) {
  return <StatusPill text={text} colour={replyByColours[tone]} />;
}

export type ChipTone = "green" | "amber" | "red" | "grey" | "accent";

const chipColours: Record<ChipTone, StatusPillColour> = {
  green: StatusPillColour.Green,
  amber: StatusPillColour.Orange,
  red: StatusPillColour.Red,
  grey: StatusPillColour.Neutral,
  accent: StatusPillColour.Blue,
};

export function Chip({ children, tone = "grey" }: { children: string; tone?: ChipTone }) {
  return <StatusPill text={children} colour={chipColours[tone]} />;
}

const tr = (key: string) => i18n.t(key, { ns: "messages" });

/** The state chip on a list row: fitment result for pre-sale, order progress for post-sale. */
export function stateChipFor(c: IInboxConversation): { text: string; tone: ChipTone } | null {
  if (c.status === "no_reply_needed") return { text: tr("No reply needed"), tone: "grey" };
  if (c.ebayCase) return { text: tr(c.ebayCase.stage === "escalated" ? "Escalated case" : c.ebayCase.kind === "item_not_received" ? "Not received case" : "Damaged case"), tone: "red" };
  if (c.type === "cancellation") return { text: tr(c.ebayCancelRequest ? "eBay cancel request" : "Cancel request"), tone: "red" };
  if (c.type === "address_change") return { text: tr("Address change"), tone: "red" };
  if (c.type === "amendment") return { text: tr("Order change"), tone: "red" };
  // The API's check found no reg or VIN on a fitment question: the buyer needs asking before anyone can answer.
  if (c.type === "fitment" && c.missing?.includes("reg") && c.status === "needs_reply") return { text: tr("Needs reg"), tone: "amber" };
  if (c.order && isLateDelivery(c.order, DateTime.utc())) return { text: tr("Late delivery"), tone: "red" };
  if (c.returnCase) return { text: tr("Return open"), tone: "red" };
  if (c.order) {
    if (c.order.shippedAt) return { text: tr("Shipped"), tone: "grey" };
    if (c.order.pickedAt) return { text: tr("Picked"), tone: "grey" };
    return { text: tr(c.order.isCollection ? "Collection" : "Paid"), tone: "grey" };
  }
  if (c.fitment) {
    if (c.fitment.overall === "fits") return { text: tr("Fits"), tone: "green" };
    if (c.fitment.overall === "check") return { text: tr("Check"), tone: "amber" };
    return { text: tr("No fit"), tone: "red" };
  }
  if (c.type === "offer") return { text: tr("Offer"), tone: "accent" };
  return null;
}

/** A UK plate. With onClick it's a button (used to run the fitment check). */
export function PlateChip({ reg, onClick, size = "sm" }: { reg: string; onClick?: () => void; size?: "sm" | "lg" }) {
  const { t } = useTranslation("messages");
  const plate = <RegPlate vrm={reg} size={size} />;
  if (!onClick) return plate;
  return (
    <button type="button" onClick={onClick} title={t("Check fitment for this reg")} className="mx-0.5 inline-flex align-baseline hover:opacity-80">
      {plate}
    </button>
  );
}

export function ResultIcon({ result, className = "h-4 w-4" }: { result: FitmentRowResult; className?: string }) {
  if (result === "match") return <CheckIcon className={classNames("text-green-700", className)} />;
  if (result === "check") return <ExclamationTriangleIcon className={classNames("text-amber-600", className)} />;
  return <XMarkIcon className={classNames("text-red-700", className)} />;
}

/** The shared Card at the side panel's size. */
export function PanelCard({ title, right, children, collapsible = false }: { title?: string; right?: ReactNode; children: ReactNode; collapsible?: boolean }) {
  return (
    <Card compact title={title} headerRight={right} collapsible={collapsible} defaultOpen={!collapsible} bodyClassName="px-4 py-3">
      {children}
    </Card>
  );
}

export function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 text-[13px]">
      <span className="shrink-0 text-gray-500">{label}</span>
      <span className="min-w-0 text-right text-gray-900">{children}</span>
    </div>
  );
}

/** Reminder that this screen is a preview on sample data: nothing is looked up or sent. */
export function PreviewNote({ children }: { children: ReactNode }) {
  return <p className="mt-2 text-[11px] italic text-gray-400">{children}</p>;
}

/**
 * True when the inbox runs on the real API ("Messages Inbox" feature flag). Anything that isn't
 * live yet (fitment lookups, warehouse alerts, AI, photos, notes, no reply needed, eBay cases)
 * only shows on the local sample data.
 */
export const InboxLiveContext = createContext(false);

/** The automatic-reply settings the cards draft from: in "off" the fitment card drafts no request; otherwise the wording is the customer's own. */
export interface IInboxAutoReplies {
  mode: "off" | "draft" | "send";
  regRequestBody: string;
  addressRequestBody: string;
}
export const InboxAutoRepliesContext = createContext<IInboxAutoReplies | undefined>(undefined);
export const useInboxAutoReplies = () => useContext(InboxAutoRepliesContext);

export const useInboxLive = () => useContext(InboxLiveContext);

/** Waiting on eBay / PartPro, which can take a while. */
export function InboxLoading({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center text-[13px] text-gray-500">
      <LoadingWheel />
      <p>{text}</p>
    </div>
  );
}

export function InboxError({ text, onRetry }: { text: string; onRetry?: () => void }) {
  const { t } = useTranslation("messages");
  return (
    <div className="m-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">
      <p>{text}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} className="mt-1.5 rounded-md border border-red-300 bg-white px-2.5 py-1 font-medium text-red-800 hover:bg-red-100">
          {t("Try again")}
        </button>
      )}
    </div>
  );
}

export type NoticeTone = "green" | "amber" | "red";

export interface IInboxNotice {
  tone: NoticeTone;
  text: string;
}

const noticeTone: Record<NoticeTone, string> = {
  green: "bg-green-50 text-green-800",
  amber: "bg-amber-50 text-amber-900",
  red: "bg-red-50 text-red-800",
};

/** The result of a real send, cancel or refund. */
export function InboxNotice({ notice, className }: { notice: IInboxNotice; className?: string }) {
  return <p className={classNames("rounded-lg px-3 py-2 text-[13px]", noticeTone[notice.tone], className)}>{notice.text}</p>;
}
